import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { daysBetween, clamp, formatDate, fromDateKey, toDateKey, stripHtml, getContrastColor } from '../utils';
import Modal from './Modal';
import Icon from './Icon';
import useTouchGesture from '../hooks/useTouchGesture';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const MIN_ZOOM      = 0.2;
const MAX_ZOOM      = 3.0;
const STEM_H        = 56;
const BAR_H_COMPACT = 32;
const BAR_H_EXPAND  = 44;
const LABEL_W       = 128;
const MAX_OFFSET    = 120;

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const getTodayKey = () => {
  const n = new Date();
  return toDateKey(n.getFullYear(), n.getMonth() + 1, n.getDate(), 'AD');
};

const safeAstro = (key) => {
  if (!key) return 0;
  if (key.length === 12) {
    const { year, era } = fromDateKey(key);
    return era === 'BC' ? 1 - year : year;
  }
  return new Date(key + 'T00:00:00').getFullYear();
};

const astroToKey = (astro) => {
  const era  = astro >= 1 ? 'AD' : 'BC';
  const year = astro >= 1 ? astro : 1 - astro;
  return toDateKey(year, 1, 1, era);
};

const formatAxisYear = (astro) => {
  if (astro >= 1)  return String(astro);
  if (astro === 0) return '1 BC';
  return `${1 - astro} BC`;
};

const getAxisInterval = (spanYears) => {
  if (spanYears <= 5)      return 1;
  if (spanYears <= 20)     return 2;
  if (spanYears <= 50)     return 5;
  if (spanYears <= 150)    return 10;
  if (spanYears <= 500)    return 50;
  if (spanYears <= 1500)   return 100;
  if (spanYears <= 5000)   return 500;
  if (spanYears <= 15000)  return 1000;
  if (spanYears <= 50000)  return 5000;
  if (spanYears <= 150000) return 10000;
  return 100000;
};

const getBandStep = (spanYears, zoom) => {
  const effective = spanYears / zoom;
  if (effective <= 50)  return 1;
  if (effective <= 500) return 10;
  return 100;
};

const safeSlug = (title) =>
  (title || 'timeline').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'timeline';

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const HorizontalTimeline = ({
  events      = [],
  colors,
  onEventClick,
  manualOrder = false,
  timeline    = null,   // optional — needed for export titles, embed, privacy checks
  isEmbed     = false,  // true when rendered inside <TimelineEmbed>
}) => {

  // ── Sort ────────────────────────────────────────────────────────────────────
  const sorted = useMemo(() => [...events].sort((a, b) => {
    if (manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    const d = (a.startDate || "").localeCompare(b.startDate || "");
    return d !== 0 ? d : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  }), [events, manualOrder]);

  // ── Safe date range (avoids hook-order issues when sorted is empty) ─────────
  const minDate   = sorted.length > 0 ? (sorted[0].startDate || '100000-01-01')                                          : '100000-01-01';
  const maxDate   = sorted.length > 0 ? (sorted[sorted.length - 1].endDate || sorted[sorted.length - 1].startDate || '100001-01-01') : '100001-01-01';
  const totalDays = daysBetween(minDate, maxDate) || 1;

  const getPct = useCallback(
    (dateStr) => clamp(daysBetween(minDate, dateStr) / totalDays * 100, 0, 100),
    [minDate, totalDays]
  );

  // ── Zoom ────────────────────────────────────────────────────────────────────
  const [zoom,        setZoom]        = useState(1.0);
  const scrollCenter  = useRef(null);   // fraction preserved across zoom changes

  const BASE_WIDTH  = useMemo(() => Math.max(800, sorted.length * 200), [sorted]);
  const scaledWidth = BASE_WIDTH * zoom;

  // ── UI state ────────────────────────────────────────────────────────────────
  const [density,       setDensity]       = useState('expanded');
  const [showYearBands, setShowYearBands] = useState(false);
  const [showMiniMap,   setShowMiniMap]   = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  const [todayOffscreen,setTodayOffscreen]= useState(false);

  // ── Drag-to-scroll ──────────────────────────────────────────────────────────
  const [isDragging, setIsDragging]   = useState(false);
  const dragStartX   = useRef(0);
  const dragStartScroll = useRef(0);

  // ── Pinch-to-zoom ───────────────────────────────────────────────────────────
  const pinch = useRef({ dist: null, baseZoom: 1 });

  // ── Mobile bottom-sheet ─────────────────────────────────────────────────────
  const [sheetEvent, setSheetEvent] = useState(null);

  // ── Export / embed ─────────────────────────────────────────────────────────
  const [isExporting,   setIsExporting]   = useState(false);
  const [exportMsg,     setExportMsg]     = useState('');
  const [embedOpen,     setEmbedOpen]     = useState(false);
  const [embedCopied,   setEmbedCopied]   = useState(false);
  const [embedWidth,    setEmbedWidth]    = useState('100%');
  const [embedHeight,   setEmbedHeight]   = useState('500');
  const [embedResp,     setEmbedResp]     = useState(false);
  const [moreOpen,      setMoreOpen]      = useState(false);
  const [morePos,       setMorePos]       = useState(null);

  // ── Collision offsets ───────────────────────────────────────────────────────
  const [labelOffsets, setLabelOffsets] = useState({});  // { [ev.id]: { yOffset, hidden } }
  const collisionTimer = useRef(null);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const scrollRef    = useRef(null);
  const containerRef = useRef(null);
  const moreRef      = useRef(null);
  const mmDragging   = useRef(false);
  const mmDragStartX = useRef(0);
  const mmDragStartScroll = useRef(0);

  // ── Momentum scroll refs ────────────────────────────────────────────────────
  const momentumRaf  = useRef(null);
  const preZoom      = useRef(zoom); // zoom at double-tap time

  // ── useTouchGesture: momentum + double-tap zoom ──────────────────────────────
  useTouchGesture(scrollRef, {
    touchAction: 'none',
    onSwipeLeft: (velocity) => {
      // Momentum scrolling after a left swipe
      if (!scrollRef.current) return;
      let v = velocity * 120;
      const decay = () => {
        if (!scrollRef.current || Math.abs(v) < 0.5) { momentumRaf.current = null; return; }
        scrollRef.current.scrollLeft += v;
        v *= 0.92;
        momentumRaf.current = requestAnimationFrame(decay);
      };
      cancelAnimationFrame(momentumRaf.current);
      momentumRaf.current = requestAnimationFrame(decay);
    },
    onSwipeRight: (velocity) => {
      if (!scrollRef.current) return;
      let v = -velocity * 120;
      const decay = () => {
        if (!scrollRef.current || Math.abs(v) < 0.5) { momentumRaf.current = null; return; }
        scrollRef.current.scrollLeft += v;
        v *= 0.92;
        momentumRaf.current = requestAnimationFrame(decay);
      };
      cancelAnimationFrame(momentumRaf.current);
      momentumRaf.current = requestAnimationFrame(decay);
    },
    onDoubleTap: (_x, _y) => {
      // Toggle between current zoom and 1.5x (or fit-all if already zoomed in)
      if (zoom > 1.05) {
        zoomToFit();
      } else {
        applyZoom(clamp(preZoom.current * 1.5, MIN_ZOOM, MAX_ZOOM));
      }
      preZoom.current = zoom;
    },
    onPinch: (scale, _mx, _my) => {
      applyZoom(clamp(zoom * scale, MIN_ZOOM, MAX_ZOOM));
    },
  });

  // ── Real-time single-finger horizontal scroll (non-passive so we can preventDefault) ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let startX = null;
    let startY = null;
    let startScroll = null;
    let axis = null; // 'h' | 'v' | null

    const onStart = (e) => {
      if (e.touches.length !== 1) return;
      el.style.scrollBehavior = 'auto'; // prevent CSS smooth from lagging behind finger
      startX     = e.touches[0].clientX;
      startY     = e.touches[0].clientY;
      startScroll = el.scrollLeft;
      axis       = null;
    };

    const onMove = (e) => {
      if (e.touches.length !== 1 || startX === null) return;
      const dx = startX - e.touches[0].clientX;
      const dy = startY - e.touches[0].clientY;

      // Determine scroll axis on first significant movement
      if (axis === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        axis = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }

      if (axis === 'h') {
        e.preventDefault(); // stop the browser from interfering
        el.scrollLeft = startScroll + dx;
      }
    };

    const onEnd = () => { el.style.scrollBehavior = ''; startX = null; startY = null; startScroll = null; axis = null; };

    el.addEventListener('touchstart',  onStart, { passive: true });
    el.addEventListener('touchmove',   onMove,  { passive: false }); // must be non-passive for preventDefault
    el.addEventListener('touchend',    onEnd,   { passive: true });
    el.addEventListener('touchcancel', onEnd,   { passive: true });

    return () => {
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []); // scrollRef is stable across renders

  // ── Today ───────────────────────────────────────────────────────────────────
  const todayKey     = getTodayKey();
  const todayInRange = minDate.length === 12 && maxDate.length === 12
    && todayKey >= minDate && todayKey <= maxDate;
  const todayPct     = todayInRange ? getPct(todayKey) : null;

  // ── Year-zero marker ────────────────────────────────────────────────────────
  const showEpochMarker = minDate.length === 12 && maxDate.length === 12
    && safeAstro(minDate) < 1 && safeAstro(maxDate) >= 1;
  const epochPct = showEpochMarker ? getPct(toDateKey(1, 1, 1, 'AD')) : null;

  // ── More-menu click-outside ──────────────────────────────────────────────────
  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const closeOnScroll = () => setMoreOpen(false);
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    window.addEventListener('scroll', closeOnScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
      window.removeEventListener('scroll', closeOnScroll, { capture: true });
    };
  }, [moreOpen]);

  // ── Fit-on-load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (timeline?.fitOnLoad && scrollRef.current) {
      const el = scrollRef.current;
      const raw = el.clientWidth / BASE_WIDTH;
      setZoom(clamp(Math.floor(raw * 20) / 20, MIN_ZOOM, MAX_ZOOM));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Zoom helpers ────────────────────────────────────────────────────────────
  const applyZoom = useCallback((newZoom) => {
    const el = scrollRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      scrollCenter.current = (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth;
    }
    setZoom(clamp(parseFloat(newZoom.toFixed(2)), MIN_ZOOM, MAX_ZOOM));
  }, []);

  const zoomIn    = useCallback(() => applyZoom(zoom + 0.25),  [zoom, applyZoom]);
  const zoomOut   = useCallback(() => applyZoom(zoom - 0.25),  [zoom, applyZoom]);
  const resetZoom = useCallback(() => applyZoom(1.0),          [applyZoom]);

  const zoomToFit = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const raw = el.clientWidth / BASE_WIDTH;
    applyZoom(Math.max(MIN_ZOOM, Math.floor(raw * 20) / 20));
  }, [BASE_WIDTH, applyZoom]);

  // Restore scroll center after zoom re-render
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollCenter.current === null) return;
    el.scrollLeft = Math.max(0, scrollCenter.current * el.scrollWidth - el.clientWidth / 2);
    scrollCenter.current = null;
  }, [zoom]);

  // ── Keyboard shortcuts (only when timeline is focused) ──────────────────────
  useEffect(() => {
    const handler = (e) => {
      const root = containerRef.current;
      if (!root || (!root.contains(document.activeElement) && root !== document.activeElement)) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === '+' || e.key === '=') { e.preventDefault(); applyZoom(zoom + 0.25); }
      if (e.key === '-')                  { e.preventDefault(); applyZoom(zoom - 0.25); }
      if (e.key === '0')                  { e.preventDefault(); applyZoom(1.0); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [zoom, applyZoom]);

  // ── Track today off-screen ──────────────────────────────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !todayInRange) return;
    const check = () => {
      const todayX = (todayPct / 100) * scaledWidth;
      setTodayOffscreen(todayX < el.scrollLeft || todayX > el.scrollLeft + el.clientWidth);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => el.removeEventListener('scroll', check);
  }, [todayInRange, todayPct, scaledWidth, zoom]);

  // ── Today navigation ────────────────────────────────────────────────────────
  const scrollToToday = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !todayInRange) return;
    const todayX = (todayPct / 100) * scaledWidth;
    el.scrollTo({ left: todayX - el.clientWidth / 2, behavior: 'smooth' });
  }, [todayInRange, todayPct, scaledWidth]);

  // ── Pointer drag-to-scroll (with 8px tap/drag disambiguation) ──────────────
  const pointerMoved = useRef(false);

  const onPointerDown = useCallback((e) => {
    if (e.pointerType === 'touch') return; // touch handled by dedicated touchmove listeners
    if (e.target.closest('button') || e.target.closest('[role="button"]')) return;
    if (e.isPrimary === false) return;
    dragStartX.current      = e.clientX;
    dragStartScroll.current = scrollRef.current?.scrollLeft ?? 0;
    pointerMoved.current    = false;
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!isDragging) return;
    const dx = dragStartX.current - e.clientX;
    if (Math.abs(dx) > 8) pointerMoved.current = true;
    if (scrollRef.current) scrollRef.current.scrollLeft = dragStartScroll.current + dx;
  }, [isDragging]);

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
    pointerMoved.current = false;
  }, []);

  // ── Pinch-to-zoom ───────────────────────────────────────────────────────────
  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      pinch.current = {
        dist: Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        ),
        baseZoom: zoom,
      };
    }
  }, [zoom]);

  const onTouchMove = useCallback((e) => {
    if (e.touches.length !== 2 || pinch.current.dist === null) return;
    e.preventDefault();
    const dist = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY,
    );
    const newZoom = clamp(pinch.current.baseZoom * (dist / pinch.current.dist), MIN_ZOOM, MAX_ZOOM);
    applyZoom(parseFloat(newZoom.toFixed(2)));
  }, [applyZoom]);

  const onTouchEnd = useCallback(() => { pinch.current.dist = null; }, []);

  // ── Mini-map drag ────────────────────────────────────────────────────────────
  const onMmPointerDown = useCallback((e) => {
    mmDragging.current      = true;
    mmDragStartX.current    = e.clientX;
    mmDragStartScroll.current = scrollRef.current?.scrollLeft ?? 0;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onMmPointerMove = useCallback((e) => {
    if (!mmDragging.current || !scrollRef.current) return;
    const mm = e.currentTarget.closest('.ht-minimap');
    if (!mm) return;
    const ratio = mm.clientWidth > 0 ? scaledWidth / mm.clientWidth : 1;
    scrollRef.current.scrollLeft = mmDragStartScroll.current + (e.clientX - mmDragStartX.current) * ratio;
  }, [scaledWidth]);

  const onMmPointerUp = useCallback(() => { mmDragging.current = false; }, []);

  const onMmClick = useCallback((e) => {
    if (mmDragging.current) return;
    const mm = e.currentTarget;
    const rect = mm.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = ratio * scaledWidth - scrollRef.current.clientWidth / 2;
    }
  }, [scaledWidth]);

  // ── Axis ticks ──────────────────────────────────────────────────────────────
  const axisTicks = useMemo(() => {
    if (minDate.length !== 12) return [];
    const startAstro = safeAstro(minDate);
    const endAstro   = safeAstro(maxDate);
    const spanYears  = Math.abs(endAstro - startAstro) || 1;
    const interval   = getAxisInterval(spanYears);
    const ticks      = [];
    const first      = Math.ceil(startAstro / interval) * interval;

    for (let astro = first; astro <= endAstro; astro += interval) {
      const key = astroToKey(astro);
      const pct = getPct(key);
      if (pct < 1 || pct > 99) continue;
      ticks.push({ pct, label: formatAxisYear(astro), astro });
    }
    return ticks;
  }, [minDate, maxDate, getPct]);

  // ── Year bands ───────────────────────────────────────────────────────────────
  const yearBands = useMemo(() => {
    if (!showYearBands || minDate.length !== 12) return [];
    const startAstro = safeAstro(minDate);
    const endAstro   = safeAstro(maxDate);
    const spanYears  = Math.abs(endAstro - startAstro) || 1;
    const step       = getBandStep(spanYears, zoom);
    const bands      = [];
    const first      = Math.floor(startAstro / step) * step;

    for (let astro = first; astro <= endAstro + step; astro += step) {
      const bandStart = astroToKey(astro);
      const bandEnd   = astroToKey(astro + step);
      const startPct  = clamp(getPct(bandStart), 0, 100);
      const endPct    = clamp(getPct(bandEnd),   0, 100);
      if (endPct <= startPct) continue;
      let label = formatAxisYear(astro);
      if (step === 10)  label = `${formatAxisYear(astro)}s`;
      if (step === 100) label = formatAxisYear(astro);
      bands.push({
        startPct, endPct,
        label,
        even: Math.floor((astro - first) / step) % 2 === 0,
      });
    }
    return bands;
  }, [showYearBands, minDate, maxDate, zoom, getPct]);

  // ── Collision detection ──────────────────────────────────────────────────────
  useEffect(() => {
    if (collisionTimer.current) clearTimeout(collisionTimer.current);
    collisionTimer.current = setTimeout(() => {
      requestAnimationFrame(() => {
        const abovePlaced = [];
        const belowPlaced = [];
        const offsets = {};

        sorted.forEach((ev, i) => {
          const sp   = getPct(ev.startDate);
          const ep   = getPct(ev.endDate);
          const w    = Math.max(ep - sp, 1);
          const cx   = ((sp + w / 2) / 100) * scaledWidth;
          const x1   = cx - LABEL_W / 2;
          const x2   = cx + LABEL_W / 2;
          const above = i % 2 === 0;
          const list  = above ? abovePlaced : belowPlaced;

          let yOffset = 0;
          for (const box of list) {
            if (x2 <= box.x1 || x1 >= box.x2) continue;
            const needed = box.yOffset + BAR_H_EXPAND + 6;
            if (yOffset < needed) yOffset = needed;
          }

          const hidden = yOffset > MAX_OFFSET;
          if (!hidden) list.push({ x1, x2, yOffset });
          offsets[ev.id] = { yOffset: hidden ? 0 : yOffset, hidden };
        });

        setLabelOffsets(offsets);
      });
    }, 200);
    return () => clearTimeout(collisionTimer.current);
  }, [sorted, scaledWidth, getPct]);

  // ── Export: full-width capture ───────────────────────────────────────────────
  // Scroll-container style manipulation is done by the CALLER so their finally
  // block restores it even when html2canvas hangs and the timeout fires first.
  const doCapture = useCallback(async () => {
    const el = containerRef.current;
    if (!el) throw new Error('Timeline element not found.');
    await document.fonts.ready;
    const isMobileExport = typeof window !== 'undefined' && window.innerWidth < 768;
    return await html2canvas(el, {
      scale:           isMobileExport ? 1 : 2,
      useCORS:         true,
      allowTaint:      true,
      backgroundColor: null,
      width:           el.scrollWidth,
      height:          el.scrollHeight,
      scrollX:         0,
      scrollY:         0,
    });
  }, []);

  // ── PNG export ───────────────────────────────────────────────────────────────
  const handleExportPNG = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Preparing export…');
    const prevZoom = zoom;
    setZoom(1.0);
    await new Promise(r => setTimeout(r, 150));

    // Expand scroll container so html2canvas captures full width.
    // Stored here so finally always restores, even when doCapture's promise
    // is still running after the race timeout fires.
    const scrollEl      = scrollRef.current;
    const origOverflow  = scrollEl?.style.overflowX ?? '';
    const origWidth     = scrollEl?.style.width     ?? '';
    if (scrollEl) {
      scrollEl.style.overflowX = 'visible';
      scrollEl.style.width     = `${scrollEl.scrollWidth}px`;
    }

    try {
      const canvas = await Promise.race([
        doCapture(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Export timed out. Try zooming out first.')), 15000)),
      ]);
      const date   = new Date().toISOString().slice(0, 10);
      const slug   = safeSlug(timeline?.title);
      const link   = document.createElement('a');
      link.download = `${slug}_${date}.png`;
      link.href     = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert(`PNG export failed: ${err.message}`);
    } finally {
      if (scrollEl) {
        scrollEl.style.overflowX = origOverflow;
        scrollEl.style.width     = origWidth;
      }
      setZoom(prevZoom);
      setIsExporting(false);
    }
  }, [zoom, doCapture, timeline]);

  // ── PDF export ───────────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Generating PDF…');
    const prevZoom = zoom;
    setZoom(1.0);
    await new Promise(r => setTimeout(r, 150));

    const scrollEl      = scrollRef.current;
    const origOverflow  = scrollEl?.style.overflowX ?? '';
    const origWidth     = scrollEl?.style.width     ?? '';
    if (scrollEl) {
      scrollEl.style.overflowX = 'visible';
      scrollEl.style.width     = `${scrollEl.scrollWidth}px`;
    }

    try {
      const canvas = await Promise.race([
        doCapture(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Export timed out. Try zooming out first.')), 15000)),
      ]);

      // ── Page constants ───────────────────────────────────────────────────────
      const pageW    = 841.89;   // A4 landscape pt
      const pageH    = 595.28;
      const MARGIN   = 36;
      const FOOTER_H = 20;
      const LINE_H   = 13;
      const contentW = pageW - MARGIN * 2;

      const titleText  = timeline?.title  || 'Timeline';
      const authorText = timeline?.author || '';
      const descRaw    = timeline?.description ? stripHtml(timeline.description).trim() : '';
      const today      = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

      // ── Pre-compute header height (title + description + author) ─────────────
      pdf.setFontSize(10);
      const descLines = descRaw ? pdf.splitTextToSize(descRaw, contentW) : [];

      let HEADER = MARGIN + 24; // title
      if (descLines.length) HEADER += descLines.length * LINE_H + 6;
      if (authorText)       HEADER += LINE_H + 4;
      HEADER += 8; // bottom padding before canvas

      // ── Pre-compute All Events data ──────────────────────────────────────────
      setExportMsg('Building events list…');
      const eventsData = sorted.map(ev => {
        const desc = stripHtml(ev.description || '').trim();
        pdf.setFontSize(10);
        const wrapped = desc ? pdf.splitTextToSize(desc, contentW) : [];
        return {
          title:   ev.title,
          date:    ev.endDate && ev.endDate !== ev.startDate ? `${formatDate(ev.startDate, ev.startEra !== 'NONE')} → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}` : formatDate(ev.startDate, ev.startEra !== 'NONE'),
          wrapped,
        };
      });

      // Simulate layout to count how many All Events pages we need
      let simY = MARGIN + LINE_H * 2.5; // after section heading
      let numEventPages = 1;
      for (const ev of eventsData) {
        const blockH = LINE_H * 1.6 + LINE_H + ev.wrapped.length * LINE_H + 10;
        if (simY + blockH > pageH - FOOTER_H - LINE_H) { simY = MARGIN + LINE_H; numEventPages++; }
        simY += blockH;
      }

      // ── Scale canvas to fit page ──────────────────────────────────────────────
      const scale2x    = 2;
      const canvasNatW = canvas.width  / scale2x;
      const canvasNatH = canvas.height / scale2x;
      const contentH   = pageH - HEADER - FOOTER_H;
      const scaleH     = contentH / canvasNatH;
      const finalScale = canvasNatW * scaleH > contentW * 3
        ? (contentW * 3) / canvasNatW
        : scaleH;
      const renderedW        = canvasNatW * finalScale;
      const renderedH        = canvasNatH * finalScale;
      const numTimelinePages = Math.ceil(renderedW / contentW);
      const totalPages       = numTimelinePages + numEventPages;

      // ── Footer helper ─────────────────────────────────────────────────────────
      const drawFooter = (pageNum) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(160, 160, 160);
        pdf.text('Chronos', MARGIN, pageH - MARGIN / 2);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - MARGIN, pageH - MARGIN / 2, { align: 'right' });
      };

      // ── Write timeline canvas pages ───────────────────────────────────────────
      setExportMsg('Rendering timeline…');
      for (let page = 0; page < numTimelinePages; page++) {
        if (page > 0) pdf.addPage();

        if (page === 0) {
          // Title
          let hy = MARGIN + 20;
          pdf.setFont('times', 'bold');
          pdf.setFontSize(20);
          pdf.setTextColor(30, 30, 30);
          pdf.text(titleText, MARGIN, hy);
          hy += 26;

          // Description
          if (descLines.length) {
            pdf.setFont('times', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(80, 80, 80);
            pdf.text(descLines, MARGIN, hy);
            hy += descLines.length * LINE_H + 6;
          }

          // Author
          if (authorText) {
            pdf.setFont('times', 'italic');
            pdf.setFontSize(11);
            pdf.setTextColor(110, 110, 110);
            pdf.text(authorText, MARGIN, hy);
          }

          // Date (top-right)
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(140, 140, 140);
          pdf.text(today, pageW - MARGIN, MARGIN + 20, { align: 'right' });
        }

        // Canvas slice
        const srcPxPerPt = canvasNatW / renderedW;
        const srcX = page * contentW * srcPxPerPt * scale2x;
        const srcW = Math.min(contentW * srcPxPerPt * scale2x, canvas.width - srcX);
        const slice = document.createElement('canvas');
        slice.width  = srcW;
        slice.height = canvas.height;
        slice.getContext('2d').drawImage(canvas, -srcX, 0);

        const dstW = (srcW / scale2x) * finalScale;
        const topY = page === 0 ? HEADER : MARGIN;
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, topY, dstW, renderedH);

        drawFooter(page + 1);
      }

      // ── Write All Events pages ────────────────────────────────────────────────
      setExportMsg('Writing events…');
      pdf.addPage();
      let evPage = numTimelinePages + 1;
      let y = MARGIN + LINE_H;

      // Section heading
      pdf.setFont('times', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(30, 30, 30);
      pdf.text(`All Events  (${eventsData.length})`, MARGIN, y);
      y += LINE_H * 2;

      // Thin rule under heading
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.5);
      pdf.line(MARGIN, y - LINE_H * 0.5, pageW - MARGIN, y - LINE_H * 0.5);

      for (const ev of eventsData) {
        const blockH = LINE_H * 1.6 + LINE_H + ev.wrapped.length * LINE_H + 10;

        if (y + blockH > pageH - FOOTER_H - LINE_H) {
          drawFooter(evPage);
          pdf.addPage();
          evPage++;
          y = MARGIN + LINE_H;
        }

        // Event title
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 30, 30);
        pdf.text(ev.title, MARGIN, y);
        y += LINE_H * 1.6;

        // Date range
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(110, 110, 110);
        pdf.text(ev.date, MARGIN, y);
        y += LINE_H;

        // Description
        if (ev.wrapped.length > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(55, 55, 55);
          pdf.text(ev.wrapped, MARGIN, y);
          y += ev.wrapped.length * LINE_H;
        }

        // Divider
        y += 4;
        pdf.setDrawColor(210, 210, 210);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, pageW - MARGIN, y);
        y += 6;
      }

      drawFooter(evPage);

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`${safeSlug(titleText)}_${date}.pdf`);
    } catch (err) {
      alert(`PDF export failed: ${err.message}`);
    } finally {
      if (scrollEl) {
        scrollEl.style.overflowX = origOverflow;
        scrollEl.style.width     = origWidth;
      }
      setZoom(prevZoom);
      setIsExporting(false);
      setExportMsg('');
    }
  }, [zoom, doCapture, timeline, sorted]);

  // ── Embed ────────────────────────────────────────────────────────────────────
  const isPublic   = timeline?.privacy === 'public';
  const embedUrl   = timeline?.id
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/${timeline.id}`
    : '';
  const safeTitle  = (timeline?.title || 'Timeline').replace(/"/g, '&quot;');
  const wAttr      = embedResp ? '100%'  : embedWidth;
  const hAttr      = embedResp ? '100%'  : embedHeight;
  const embedCode  = `<iframe\n  src="${embedUrl}"\n  width="${wAttr}"\n  height="${hAttr}"\n  style="border:none;border-radius:12px;"\n  title="${safeTitle}"\n  allowfullscreen\n></iframe>`;

  const copyEmbed = useCallback(() => {
    navigator.clipboard?.writeText(embedCode).catch(() => {});
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  }, [embedCode]);

  // ── Mobile event handler: bottom sheet on narrow screens ────────────────────
  // Ignore click if the pointer moved > 8px (it was a drag, not a tap)
  const handleBarClick = useCallback((ev) => {
    if (pointerMoved.current) return;
    onEventClick?.(ev);
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setSheetEvent(ev);
    }
  }, [onEventClick]);

  // ── Empty state (all hooks are above this point) ─────────────────────────────
  if (sorted.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
        No events yet. Add your first event.
      </div>
    );
  }

  // ── Per-event geometry ───────────────────────────────────────────────────────
  const barH = density === 'compact' ? BAR_H_COMPACT : BAR_H_EXPAND;

  // Space above/below axis: stem + max collision offset + ~90px for tallest pill
  const ABOVE_H = STEM_H + MAX_OFFSET + 110;
  const BELOW_H = STEM_H + MAX_OFFSET + 110;

  // ── Scroll state for minimap viewport indicator ───────────────────────────────
  // We read scrollLeft directly from DOM on render (not reactive — minimap updates
  // via scroll listener via the minimap's onScroll in the outer div).
  const scrollEl     = scrollRef.current;
  const scrollLeft   = scrollEl?.scrollLeft   ?? 0;
  const viewportW    = scrollEl?.clientWidth  ?? 0;
  const mmViewLeft   = scaledWidth > 0 ? (scrollLeft   / scaledWidth) * 100 : 0;
  const mmViewWidth  = scaledWidth > 0 ? (viewportW    / scaledWidth) * 100 : 100;

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="ht-root"
      tabIndex={-1}
      data-exporting={isExporting || undefined}
    >

      {/* ── Print-only header ─────────────────────────────────────────────── */}
      <div className="ht-print-header" aria-hidden="true">
        <span className="ht-print-title">{timeline?.title || 'Timeline'}</span>
        {timeline?.author && <span className="ht-print-author">by {timeline.author}</span>}
        <span className="ht-print-date">Printed {new Date().toLocaleDateString()}</span>
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      {!isEmbed && (
        <div className="ht-toolbar ht-no-print" role="toolbar" aria-label="Timeline controls">

          {/* Zoom controls — always visible */}
          <div className="ht-zoom-group">
            <button className="ht-btn" onClick={zoomOut} title="Zoom out (Ctrl/⌘ −)" aria-label="Zoom out">
              <Icon name="zoomOut" size={14} />
            </button>
            <span className="ht-zoom-pct" aria-live="polite">{Math.round(zoom * 100)}%</span>
            <button className="ht-btn" onClick={zoomIn} title="Zoom in (Ctrl/⌘ +)" aria-label="Zoom in">
              <Icon name="zoomIn" size={14} />
            </button>
            <button className="ht-btn" onClick={resetZoom} title="Reset zoom (Ctrl/⌘ 0)">Reset</button>
            <button className="ht-btn" onClick={zoomToFit} title="Fit all events in view">
              <Icon name="maximize2" size={14} />
              Fit
            </button>
          </div>

          {/* Desktop: separator + secondary controls inline */}
          <div className="ht-toolbar-sep ht-desktop-only" />
          <div className="ht-secondary-inline">
            <button
              className={`ht-btn ${density === 'compact' ? 'ht-btn-active' : ''}`}
              onClick={() => setDensity(d => d === 'expanded' ? 'compact' : 'expanded')}
              title={density === 'expanded' ? 'Switch to compact view' : 'Switch to expanded view'}
            >
              <Icon name={density === 'compact' ? 'expand2' : 'compress'} size={13} />
              {density === 'compact' ? 'Expanded' : 'Compact'}
            </button>
            <button
              className={`ht-btn ${showYearBands ? 'ht-btn-active' : ''}`}
              onClick={() => setShowYearBands(v => !v)}
              title="Toggle year bands"
            >
              <Icon name="layers" size={13} />
              Bands
            </button>
            <button
              className={`ht-btn ${showMiniMap ? 'ht-btn-active' : ''}`}
              onClick={() => setShowMiniMap(v => !v)}
              title="Toggle mini-map"
            >
              <Icon name="minimap" size={13} />
              Map
            </button>
            {todayInRange && todayOffscreen && (
              <button className="ht-btn ht-btn-today" onClick={scrollToToday} title="Scroll to today">
                <Icon name="calToday" size={13} />
                Today
              </button>
            )}
            <div className="ht-toolbar-sep" />
            <button className="ht-btn ht-btn-export" onClick={handleExportPNG} title="Export as PNG">
              <Icon name="image" size={13} />
              PNG
            </button>
            <button className="ht-btn ht-btn-export" onClick={handleExportPDF} title="Export as PDF">
              <Icon name="fileText" size={13} />
              PDF
            </button>
            {timeline && (
              <button
                className="ht-btn ht-btn-export"
                onClick={() => isPublic && setEmbedOpen(true)}
                disabled={!isPublic}
                title={isPublic ? 'Get embed code' : 'Make this timeline public to enable embedding'}
                aria-disabled={!isPublic}
              >
                <Icon name="code" size={13} />
                Embed
              </button>
            )}
          </div>

          {/* Mobile: More dropdown */}
          <div className="ht-more-wrapper" ref={moreRef}>
            <button
              className={`ht-btn ${moreOpen ? 'ht-btn-active' : ''}`}
              onClick={(e) => {
                if (!moreOpen) {
                  const r = e.currentTarget.getBoundingClientRect();
                  setMorePos({ top: r.bottom + 6, right: window.innerWidth - r.right });
                }
                setMoreOpen(o => !o);
              }}
              aria-label="More controls"
              aria-expanded={moreOpen}
            >
              <Icon name="moreHorizontal" size={14} fill="currentColor" />
              More
            </button>
            {moreOpen && morePos && (
              <div className="ht-more-dropdown" style={{ top: morePos.top, right: morePos.right }}>
                <button
                  className={`ht-btn ${density === 'compact' ? 'ht-btn-active' : ''}`}
                  onClick={() => setDensity(d => d === 'expanded' ? 'compact' : 'expanded')}
                  title={density === 'expanded' ? 'Switch to compact view' : 'Switch to expanded view'}
                >
                  <Icon name={density === 'compact' ? 'expand2' : 'compress'} size={13} />
                  {density === 'compact' ? 'Expanded' : 'Compact'}
                </button>
                <button
                  className={`ht-btn ${showYearBands ? 'ht-btn-active' : ''}`}
                  onClick={() => setShowYearBands(v => !v)}
                  title="Toggle year bands"
                >
                  <Icon name="layers" size={13} />
                  Bands
                </button>
                <button
                  className={`ht-btn ${showMiniMap ? 'ht-btn-active' : ''}`}
                  onClick={() => setShowMiniMap(v => !v)}
                  title="Toggle mini-map"
                >
                  <Icon name="minimap" size={13} />
                  Map
                </button>
                {todayInRange && todayOffscreen && (
                  <button className="ht-btn ht-btn-today" onClick={scrollToToday} title="Scroll to today">
                    <Icon name="calToday" size={13} />
                    Today
                  </button>
                )}
                <div className="ht-toolbar-sep" style={{ width: '100%', height: 1, margin: '4px 0' }} />
                <button className="ht-btn ht-btn-export" onClick={handleExportPNG} title="Export as PNG">
                  <Icon name="image" size={13} />
                  PNG
                </button>
                <button className="ht-btn ht-btn-export" onClick={handleExportPDF} title="Export as PDF">
                  <Icon name="fileText" size={13} />
                  PDF
                </button>
                {timeline && (
                  <button
                    className="ht-btn ht-btn-export"
                    onClick={() => { if (isPublic) { setEmbedOpen(true); setMoreOpen(false); } }}
                    disabled={!isPublic}
                    title={isPublic ? 'Get embed code' : 'Make this timeline public to enable embedding'}
                    aria-disabled={!isPublic}
                  >
                    <Icon name="code" size={13} />
                    Embed
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Today outside range indicator ─────────────────────────────────── */}
      {!todayInRange && minDate.length === 12 && (() => {
        const tKey = getTodayKey();
        if (tKey < minDate) {
          const days = Math.abs(daysBetween(tKey, minDate));
          return (
            <div className="ht-outside-range ht-no-print" aria-live="polite">
              Today is {days.toLocaleString()} day{days !== 1 ? 's' : ''} before this timeline starts
            </div>
          );
        }
        if (tKey > maxDate) {
          const days = Math.abs(daysBetween(maxDate, tKey));
          return (
            <div className="ht-outside-range ht-no-print" aria-live="polite">
              This timeline ended {days.toLocaleString()} day{days !== 1 ? 's' : ''} ago
            </div>
          );
        }
        return null;
      })()}

      {/* ── Main scroll container ─────────────────────────────────────────── */}
      <div
        ref={scrollRef}
        className={`ht-scroll${isDragging ? ' ht-grabbing' : ' ht-grab'}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Inner div scaled by zoom */}
        <div
          className="ht-inner"
          style={{ width: scaledWidth, paddingLeft: 80, paddingRight: 80, boxSizing: 'border-box' }}
        >
          {/* ── Above-row label space ─────────────────────────────────────── */}
          <div style={{ height: ABOVE_H }} aria-hidden="true" />

          {/* ── Year bands ───────────────────────────────────────────────── */}
          {showYearBands && yearBands.map((band, i) => (
            <div
              key={i}
              className={`ht-band ${band.even ? 'ht-band-even' : 'ht-band-odd'}`}
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: ABOVE_H - STEM_H,
                bottom: BELOW_H - STEM_H,
                left: `calc(80px + ${band.startPct}% * (100% - 160px) / 100)`,
                width: `calc(${band.endPct - band.startPct}% * (100% - 160px) / 100)`,
              }}
            >
              <span className="ht-band-label">{band.label}</span>
            </div>
          ))}

          {/* ── Axis tick labels (above the track) ───────────────────────── */}
          <div style={{ position: 'relative', height: 0 }} aria-hidden="true">
            {/* Intermediate ticks — rendered first so endpoints paint on top */}
            {axisTicks.map((tick, i) => (
              <span
                key={i}
                className="ht-axis-tick"
                style={{ left: `${tick.pct}%`, color: colors.secondary }}
                aria-hidden="true"
              >
                {tick.label}
              </span>
            ))}

            {/* Min/max endpoint labels — rendered last so they're never covered by ticks */}
            <span className="ht-axis-endpoint ht-axis-start" style={{ color: colors.primary, background: colors.tertiary, padding: '1px 3px', borderRadius: 3, zIndex: 3 }}>
              {formatDate(minDate)}
            </span>
            <span className="ht-axis-endpoint ht-axis-end" style={{ color: colors.primary, background: colors.tertiary, padding: '1px 3px', borderRadius: 3, zIndex: 3 }}>
              {formatDate(maxDate)}
            </span>
          </div>

          {/* ── Track line ────────────────────────────────────────────────── */}
          <div
            role="list"
            aria-label="Timeline events"
            className="ht-track"
            style={{ background: `${colors.secondary}40` }}
          >
            {/* Gradient fill */}
            <div
              className="ht-track-fill"
              style={{ background: `linear-gradient(90deg, ${colors.secondary}, ${colors.primary})` }}
              aria-hidden="true"
            />

            {/* ── Year zero (epoch) marker ───────────────────────────────── */}
            {showEpochMarker && epochPct !== null && (
              <div
                className="ht-epoch-marker"
                style={{ left: `${epochPct}%` }}
                aria-label="Year 0 AD / 1 BC epoch boundary"
              >
                <div className="ht-epoch-line" style={{ borderColor: colors.secondary }} />
                <span className="ht-epoch-label" style={{ background: colors.secondary, color: getContrastColor(colors.secondary) }}>
                  0 AD / 1 BC
                </span>
              </div>
            )}

            {/* ── Today marker ──────────────────────────────────────────── */}
            {todayInRange && todayPct !== null && (
              <div
                className="ht-today-marker"
                style={{ left: `${todayPct}%` }}
                aria-label={`Today: ${formatDate(todayKey)}`}
              >
                <div className="ht-today-line" />
                <span className="ht-today-label">Today</span>
              </div>
            )}

            {/* ── Event bars + labels ────────────────────────────────────── */}
            {sorted.map((ev, i) => {
              const startPct = getPct(ev.startDate);
              const endPct   = getPct(ev.endDate);
              const width    = Math.max(endPct - startPct, 1);
              const above    = i % 2 === 0;
              const info     = labelOffsets[ev.id];
              const yOff     = info?.yOffset  ?? 0;
              const hidden   = info?.hidden   ?? false;
              const desc     = density === 'expanded' && !hidden
                ? (ev.description ? stripHtml(ev.description).slice(0, 120) : '')
                : '';
              const photo    = density === 'expanded' && ev.photo ? ev.photo : null;

              // Accessible label
              const ariaLabel = `${ev.title}, ${formatDate(ev.startDate, ev.startEra !== 'NONE')} to ${formatDate(ev.endDate, ev.endEra !== 'NONE')}`;

              return (
                <div key={ev.id} role="listitem">
                  {/* ── Visual bar (44px min touch target via padding trick) ── */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={ariaLabel}
                    className="ht-bar"
                    onClick={() => handleBarClick(ev)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBarClick(ev); } }}
                    style={{
                      position:   'absolute',
                      left:       `${startPct}%`,
                      width:      `max(${width}%, 2px)`,
                      background: colors.primary,
                    }}
                  >
                    {/* Transparent hit extension to reach 44px min width */}
                    <span className="ht-bar-hit" aria-hidden="true" />
                  </div>

                  {/* ── Label (above or below) ──────────────────────────── */}
                  {!hidden && (
                    <div
                      aria-hidden="true"
                      className="ht-label-wrap"
                      style={{
                        position:  'absolute',
                        left:      `${startPct + width / 2}%`,
                        // Bottom-anchor above labels so stem tip lands on bar top (2px above track).
                        // Top-anchor below labels so stem tip lands on bar bottom (6px below track).
                        ...(above ? { bottom: 6 } : { top: 6 }),
                        transform: 'translateX(-50%)',
                        display:   'flex',
                        // above=column: pill at top (far from bar), stem hangs down to bar.
                        // below=column-reverse: stem at top (connects to bar), pill hangs below.
                        flexDirection: above ? 'column' : 'column-reverse',
                        alignItems: 'center',
                      }}
                    >
                      {/* Label pill */}
                      <button
                        onClick={() => handleBarClick(ev)}
                        tabIndex={-1}
                        className="ht-label-pill"
                        style={{
                          background:  `${colors.secondary}22`,
                          border:      `1px solid ${colors.primary}60`,
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = colors.secondary;
                          e.currentTarget.style.background  = `${colors.secondary}44`;
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = `${colors.primary}60`;
                          e.currentTarget.style.background  = `${colors.secondary}22`;
                        }}
                      >
                        {/* Photo thumbnail (expanded mode only) */}
                        {photo && (
                          <img
                            src={photo}
                            alt=""
                            aria-hidden="true"
                            className="ht-thumb"
                            loading={i < 3 ? 'eager' : 'lazy'}
                          />
                        )}
                        <span className="ht-label-title" style={{ color: colors.primary }}>
                          {ev.title}
                        </span>
                        {density === 'expanded' && (
                          <span className="ht-label-date" style={{ color: colors.secondary }}>
                            {formatDate(ev.startDate, ev.startEra !== 'NONE')}
                          </span>
                        )}
                        {desc && (
                          <span className="ht-label-desc" style={{ color: colors.secondary }}>
                            {desc.slice(0, 80)}
                          </span>
                        )}
                      </button>

                      {/* Stem — height grows with yOff to push pill further from bar on collision */}
                      <div
                        className="ht-stem"
                        style={{
                          height:      STEM_H + yOff,
                          background:  `${colors.primary}60`,
                        }}
                      />
                    </div>
                  )}

                  {/* Numbered indicator when label is hidden due to collision overflow */}
                  {hidden && (
                    <button
                      className="ht-hidden-label"
                      onClick={() => handleBarClick(ev)}
                      title={ariaLabel}
                      style={{
                        position:    'absolute',
                        left:        `${startPct + width / 2}%`,
                        top:         above ? -(barH + 8) : barH,
                        transform:   'translateX(-50%)',
                        background:  colors.primary,
                        color:       getContrastColor(colors.primary),
                      }}
                    >
                      {i + 1}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Below-row label space ─────────────────────────────────────── */}
          <div style={{ height: BELOW_H }} aria-hidden="true" />
        </div>
      </div>

      {/* ── Mini-map ──────────────────────────────────────────────────────── */}
      {showMiniMap && (
        <div
          className="ht-minimap ht-no-print"
          role="scrollbar"
          aria-label="Timeline overview — click or drag to navigate"
          aria-controls="ht-scroll"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(mmViewLeft)}
          onClick={onMmClick}
        >
          {/* Event bars (compressed) */}
          {sorted.map((ev) => {
            const sp = getPct(ev.startDate);
            const ep = getPct(ev.endDate);
            const w  = Math.max(ep - sp, 0.5);
            return (
              <div
                key={ev.id}
                className="ht-mm-bar"
                style={{
                  left:       `${sp}%`,
                  width:      `${w}%`,
                  background: colors.secondary,
                }}
                aria-hidden="true"
              />
            );
          })}

          {/* Viewport indicator — draggable */}
          <div
            className="ht-mm-viewport"
            style={{
              left:  `${Math.min(mmViewLeft, 100 - mmViewWidth)}%`,
              width: `${Math.max(mmViewWidth, 4)}%`,
            }}
            onPointerDown={onMmPointerDown}
            onPointerMove={onMmPointerMove}
            onPointerUp={onMmPointerUp}
            onPointerCancel={onMmPointerUp}
            aria-hidden="true"
          />
        </div>
      )}

      {/* ── Export loading overlay ────────────────────────────────────────── */}
      {isExporting && (
        <div className="ht-export-overlay" role="status" aria-label={exportMsg}>
          <div className="ht-export-card">
            <div className="export-spinner" aria-hidden="true" />
            <p className="ht-export-msg">{exportMsg}</p>
          </div>
        </div>
      )}

      {/* ── Mobile bottom sheet ────────────────────────────────────────────── */}
      {sheetEvent && (
        <div
          className="ht-sheet-overlay"
          onClick={() => setSheetEvent(null)}
          role="dialog"
          aria-modal="true"
          aria-label={sheetEvent.title}
        >
          <div className="ht-sheet" onClick={e => e.stopPropagation()}>
            <div className="ht-sheet-handle" aria-hidden="true" />
            <button
              className="ht-sheet-close"
              onClick={() => setSheetEvent(null)}
              aria-label="Close event detail"
            >
              <Icon name="close" size={18} />
            </button>
            <h3 className="ht-sheet-title" style={{ color: colors.primary }}>{sheetEvent.title}</h3>
            <p className="ht-sheet-date" style={{ color: colors.secondary }}>
              {formatDate(sheetEvent.startDate, sheetEvent.startEra !== 'NONE')}
              {sheetEvent.endDate !== sheetEvent.startDate && ` → ${formatDate(sheetEvent.endDate, sheetEvent.endEra !== 'NONE')}`}
            </p>
            {sheetEvent.photo && (
              <img src={sheetEvent.photo} alt={sheetEvent.title} className="ht-sheet-photo" loading="lazy" />
            )}
            {sheetEvent.videoUrl && (() => {
              try {
                const u    = new URL(sheetEvent.videoUrl.trim());
                const host = u.hostname.replace('www.', '').replace('m.', '');
                let id = null;
                if (host === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
                else if (host === 'youtube.com' || host === 'youtube-nocookie.com') id = (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/')) ? u.pathname.split('/')[2] : u.searchParams.get('v');
                const embedSrc = (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
                if (!embedSrc) return null;
                return (
                  <div key="yt" style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden', marginBottom: 12, background: 'var(--bg2)' }}>
                    <iframe
                      src={embedSrc}
                      title={sheetEvent.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                    />
                  </div>
                );
              } catch { return null; }
            })()}
            {sheetEvent.description && (
              <p className="ht-sheet-desc">{stripHtml(sheetEvent.description)}</p>
            )}
          </div>
        </div>
      )}

      {/* ── Embed code modal ─────────────────────────────────────────────────── */}
      <Modal
        open={embedOpen}
        onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }}
        title="Embed Timeline"
        width={580}
      >
        <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
          Paste this snippet into any webpage to embed{' '}
          <strong style={{ color: 'var(--text)' }}>{timeline?.title}</strong> as an interactive iframe.
        </p>

        {/* Code block */}
        <div style={{
          background: 'var(--bg3)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12,
        }}>
          <div style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>HTML</span>
          </div>
          <pre style={{
            padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75,
            color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto',
            whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {embedCode}
          </pre>
        </div>

        {/* Size controls */}
        <div className="ht-embed-controls">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={embedResp} onChange={e => setEmbedResp(e.target.checked)} style={{ width: 'auto' }} />
            Responsive (100% width)
          </label>
          {!embedResp && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label style={{ marginBottom: 4 }}>Width</label>
                <input
                  value={embedWidth}
                  onChange={e => setEmbedWidth(e.target.value)}
                  placeholder="e.g. 900 or 100%"
                  style={{ fontSize: 13 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ marginBottom: 4 }}>Height (px)</label>
                <input
                  value={embedHeight}
                  onChange={e => setEmbedHeight(e.target.value)}
                  placeholder="e.g. 500"
                  style={{ fontSize: 13 }}
                />
              </div>
            </div>
          )}
          {/* Preset sizes */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['Small', '600', '400'], ['Medium', '900', '500'], ['Large', '1200', '600']].map(([name, w, h]) => (
              <button
                key={name}
                className="ht-btn"
                onClick={() => { setEmbedWidth(w); setEmbedHeight(h); setEmbedResp(false); }}
                style={{ fontSize: 12 }}
              >
                {name} ({w}×{h})
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn-ghost" onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}>
            Close
          </button>
          <button
            className="btn-primary"
            onClick={copyEmbed}
            style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 148 }}
          >
            <Icon name={embedCopied ? 'check' : 'copy'} size={14} />
            {embedCopied ? 'Copied!' : 'Copy Snippet'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default HorizontalTimeline;
